import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { 
    MapPin, 
    Calendar,
    HandshakeIcon,
    Building2,
    CheckCircle,
    XCircle,
    Clock,
    Loader2,
    Search,
    UserPlus,
    Trash,
    AlertCircle,
    Timer, Sparkles } from 'lucide-react';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "@/components/ui/avatar";
import {
    Dialog,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Alert,
    AlertDescription,
} from "@/components/ui/alert";
import type { PropertyListings } from '@/types/PropertyListings';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AIMarketingPanel from '@/components/listings/AIMarketingPanel';
import Helper from '@/lib/helper';
import { toast } from 'sonner';
import api from '@/lib/axiosInstance';
import { format } from 'date-fns';
import Proposal from './view/proposal';
import Details from './view/details';
import Stats from './view/stats';
import Histories from './view/histories';
import { useSelector } from 'react-redux';
import type { RootState } from '@/lib/redux/store';
import type { PropertyListingSharedAgents } from '@/types/PropertyListingSharedAgents';
import Documents from './view/documents';
import Timeline from './view/timeline';
import { Spinner } from '@/components/ui/shadcn-io/spinner';

interface CoAgencyInvite {
    id: number;
    status: number;
    createdAt: string;
    agent: {
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber: string;
        renNo: string;
        avatar: string;
    };
}

interface Agent {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    renNo: string;
    avatar: string;
}

const ListingView = () => {
    const agent = useSelector((state: RootState) => state.auth.agent);
    const { listingId } = useParams();
    const [activeTab, setActiveTab] = useState<string>('overview');
    const [listing, setListing] = useState<PropertyListings | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [showCoAgencySheet, setShowCoAgencySheet] = useState<boolean>(false);
    const [coAgencyRequests, setCoAgencyRequests] = useState<CoAgencyInvite[]>([]);
    const [loadingCoAgency, setLoadingCoAgency] = useState<boolean>(false);
    const [showCoShareSheet, setShowCoShareSheet] = useState<boolean>(false);
    const [coShareRequests, setCoShareRequests] = useState<PropertyListingSharedAgents[]>([]);
    const [loadingCoShare, setLoadingCoShare] = useState<boolean>(false);
    const [processingRequests, setProcessingRequests] = useState<{ [key: number]: 'approving' | 'rejecting' | null }>({});
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [searchResults, setSearchResults] = useState<Agent[]>([]);
    const [searchingAgents, setSearchingAgents] = useState<boolean>(false);
    const [addingAgent, setAddingAgent] = useState<{ [key: number]: boolean }>({});
    const [actionLoading, setActionLoading] = useState<boolean>(false);
    const menu = ['overview', 'stats', 'visits', 'proposal', 'documents', 'timeline'];
    const [open, setOpen] = useState<boolean>(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const [inviteConfirmDialog, setInviteConfirmDialog] = useState<{ open: boolean; agentId: number | null; agentName: string }>({
        open: false,
        agentId: null,
        agentName: ''
    });
    const [approveConfirmDialog, setApproveConfirmDialog] = useState<{ open: boolean; requestId: number | null; agentName: string }>({
        open: false,
        requestId: null,
        agentName: ''
    });
    const [rejectConfirmDialog, setRejectConfirmDialog] = useState<{ open: boolean; requestId: number | null; agentName: string }>({
        open: false,
        requestId: null,
        agentName: ''
    });
    
    const pendingCoShareRequestsCount = coShareRequests.filter(r => r.status === 0 && r.type === 1).length;
    
    // Check if current agent has access to full listing details
    const hasFullAccess = () => {
        if (!listing) return false;
        
        // If agent created the listing, they have full access
        if (listing.createdBy === agent.id) return true;
        
        // If agent is in sharedAgents, they have full access
        if (listing.sharedAgents && listing.sharedAgents.some(sa => sa.agentId === agent.id)) {
            return true;
        }
        
        return false;
    };
    
    const handleTabClick = (tab: string) => {
        // Only allow tab change if user has full access
        if (!hasFullAccess() && tab !== 'overview') {
            return;
        }
        
        setActiveTab(tab);
        setSearchParams({ tab });
    }

    const getListing = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/listings/${listingId}/stats`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            const listing = response.data.listing;
            // if(listing.createdBy !== agent.id) {
            //     if(!listing.sharedAgents.find((x: PropertyListingSharedAgents) => x.agentId === agent.id)) {
            //         toast.error('Unauthorized access.');
            //         navigate('/listings');
            //     }
            // }
            setListing(listing);
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setLoading(false);
        }
    }

    const getCoShareRequests = async () => {
        if (!listingId) return;
        
        try {
            setLoadingCoShare(true);
            const response = await api.get(`/listings/${listingId}/co-share/requests`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            
            const requests = response.data.requests || [];
            setCoShareRequests(requests);
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setLoadingCoShare(false);
        }
    }

    const getCoAgencyRequests = async () => {
        if (!listingId) return;
        
        try {
            setLoadingCoAgency(true);
            const response = await api.get(`/listings/${listingId}/co-agency/requests`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            
            const requests = response.data.requests || [];
            setCoAgencyRequests(requests);
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setLoadingCoAgency(false);
        }
    }

    const closeListing = async () => {
        try {
            if(!listing) {
                return
            }
            setActionLoading(true);
            const response = await api.post(`/listings/${listingId}/close`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setListing({
                ...listing,
                closed: 1
            })
            toast.success('Successfully marked listing as completed.');
        } catch (err) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setActionLoading(false);
            setOpen(false);
        }
    }

    const openListing = async () => {
        try {
            if(!listing) {
                return
            }
            setActionLoading(true);
            const response = await api.post(`/listings/${listingId}/open`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            toast.success('Listing reopened.');
            setListing({
                ...listing,
                closed: 0
            })
        } catch (err) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setActionLoading(false);
            setOpen(false);
        }
    }

    const removeMeFromListing = async (listingId: number, sharedId: number, type:number) => {
        try {
            const url = (type === 1 ? `/listings/${listingId}/co-agency/${sharedId}/remove` : `/listings/${listingId}/co-share/${sharedId}/remove`);
            const response = await api.delete(url);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            navigate('/listings?page=1&length=10&type=active');
        } catch (err) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    }

    const searchAgents = async (query: string) => {
        if (!query.trim() || query.length < 2) {
            setSearchResults([]);
            return;
        }
        
        try {
            setSearchingAgents(true);
            const response = await api.get(`/agents?search=${encodeURIComponent(query)}`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setSearchResults(response.data.agents || []);
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setSearchingAgents(false);
        }
    }

    const handleAddCoShareClick = (agentId: number, agentFirstName: string, agentLastName: string) => {
        setInviteConfirmDialog({
            open: true,
            agentId,
            agentName: `${agentFirstName} ${agentLastName}`
        });
    }

    const handleAddCoShare = async () => {
        if (!listingId || !inviteConfirmDialog.agentId) return;
        
        try {
            setAddingAgent(prev => ({ ...prev, [inviteConfirmDialog.agentId!]: true }));
            
            const response = await api.post(`/listings/${listingId}/co-share/invite`, {
                agentId: inviteConfirmDialog.agentId
            });
            
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            
            toast.success('Co-share invitation sent successfully');
            setSearchQuery('');
            setSearchResults([]);
            getCoShareRequests();
            setInviteConfirmDialog({ open: false, agentId: null, agentName: '' });
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            if (inviteConfirmDialog.agentId) {
                setAddingAgent(prev => ({ ...prev, [inviteConfirmDialog.agentId!]: false }));
            }
        }
    }

    const handleApproveRequestClick = (requestId: number, agentFirstName: string, agentLastName: string) => {
        setApproveConfirmDialog({
            open: true,
            requestId,
            agentName: `${agentFirstName} ${agentLastName}`
        });
    }

    const handleApproveRequest = async () => {
        if (!listingId || !approveConfirmDialog.requestId) return;
        
        try {
            setProcessingRequests(prev => ({ ...prev, [approveConfirmDialog.requestId!]: 'approving' }));
            
            const response = await api.post(`/listings/${listingId}/co-share/${approveConfirmDialog.requestId}/approve`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            
            toast.success('Request approved');
            getCoShareRequests();
            setApproveConfirmDialog({ open: false, requestId: null, agentName: '' });
        } catch (err: any) {
            if (approveConfirmDialog.requestId) {
                setProcessingRequests(prev => ({ ...prev, [approveConfirmDialog.requestId!]: null }));
            }
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    }

    const handleRejectRequestClick = (requestId: number, agentFirstName: string, agentLastName: string) => {
        setRejectConfirmDialog({
            open: true,
            requestId,
            agentName: `${agentFirstName} ${agentLastName}`
        });
    }

    const handleRejectRequest = async () => {
        if (!listingId || !rejectConfirmDialog.requestId) return;
        
        try {
            setProcessingRequests(prev => ({ ...prev, [rejectConfirmDialog.requestId!]: 'rejecting' }));
            
            const response = await api.post(`/listings/${listingId}/co-share/${rejectConfirmDialog.requestId}/decline`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            
            toast.success('Request declined');
            getCoShareRequests();
            setRejectConfirmDialog({ open: false, requestId: null, agentName: '' });
        } catch (err: any) {
            if (rejectConfirmDialog.requestId) {
                setProcessingRequests(prev => ({ ...prev, [rejectConfirmDialog.requestId!]: null }));
            }
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    }

    const getStatusBadge = (status: number) => {
        switch(status) {
            case 0:
                return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
            case 1:
                return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
            case 99:
                return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
            default:
                return null;
        }
    }

    const handleAddCoAgency = async (agentId: number) => {
        if (!listingId) return;
        
        try {
            setAddingAgent(prev => ({ ...prev, [agentId]: true }));
            
            const response = await api.post(`/listings/${listingId}/co-agency/invite`, {
                agentId
            });
            
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            
            toast.success('Co-agency invitation sent successfully');
            setSearchQuery('');
            setSearchResults([]);
            getCoAgencyRequests();
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setAddingAgent(prev => ({ ...prev, [agentId]: false }));
        }
    }

    const removeCoAgencyAgent = async (coAgencyId: number) => {
        if (!listingId) return;
        
        try {
            const response = await api.delete(`/listings/${listingId}/co-agency/${coAgencyId}/remove`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            toast.success('Co-agency agent removed successfully');
            setSearchQuery('');
            setSearchResults([]);
            getCoAgencyRequests();
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    }

    const cancelCoAgencyInvite = async (coAgencyId: number) => {
        if (!listingId) return;
        
        try {
            const response = await api.delete(`/listings/${listingId}/co-agency/${coAgencyId}/invite/cancel`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            toast.success('Co-agency invitation cancelled');
            setSearchQuery('');
            setSearchResults([]);
            getCoAgencyRequests();
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    }

    const removeCoShareAgent = async (coShareId: number) => {
        if (!listingId) return;
        
        try {
            const response = await api.delete(`/listings/${listingId}/co-share/${coShareId}/remove`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            toast.success('Co-share agent removed successfully');
            setSearchQuery('');
            setSearchResults([]);
            getCoShareRequests();
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    }

    const cancelCoShareInvite = async (coShareId: number) => {
        if (!listingId) return;
        
        try {
            const response = await api.delete(`/listings/${listingId}/co-share/${coShareId}/invite/cancel`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            toast.success('Co-share invitation cancelled');
            setSearchQuery('');
            setSearchResults([]);
            getCoShareRequests();
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    }

    useEffect(() => {
        getListing();
        getCoShareRequests();
        getCoAgencyRequests();
    }, []);

    useEffect(() => {
        if (!showCoShareSheet) {
            setSearchQuery('');
            setSearchResults([]);
        }
    }, [showCoShareSheet]);

    useEffect(() => {
        if (!showCoAgencySheet) {
            setSearchQuery('');
            setSearchResults([]);
        }
    }, [showCoAgencySheet]);

    useEffect(() => {
        const debounce = setTimeout(() => {
            if (searchQuery) {
                searchAgents(searchQuery);
            }
        }, 300);

        return () => clearTimeout(debounce);
    }, [searchQuery]);

    useEffect(() => {
        const tabFromUrl = searchParams.get('tab');
        if (tabFromUrl && menu.includes(tabFromUrl)) {
            setActiveTab(tabFromUrl);
        }
    }, [searchParams]);

    // New useEffect to handle access control when listing loads
    useEffect(() => {
        if (listing && !hasFullAccess()) {
            // If user doesn't have full access and is on any tab other than overview
            if (activeTab !== 'overview') {
                setActiveTab('overview');
                setSearchParams({ tab: 'overview' });
            }
        }
    }, [listing]);

    if (loading) {
        return (
            <div className="min-h-screen bg-background p-0">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-8">
                        <div className="mb-3 flex items-start justify-between">
                            <div className="flex-1">
                                <Skeleton className="h-8 w-3/4 mb-3" />
                                <div className="flex items-center gap-3">
                                    <Skeleton className="h-6 w-24" />
                                    <Skeleton className="h-6 w-24" />
                                </div>
                            </div>
                            <div className="text-right">
                                <Skeleton className="h-4 w-32 mb-2" />
                                <Skeleton className="h-3 w-28" />
                            </div>
                        </div>
                        <div className="flex items-center gap-6">
                            <Skeleton className="h-4 w-48" />
                            <Skeleton className="h-4 w-32" />
                        </div>
                    </div>

                    <div className="mb-8">
                        <div className="flex gap-4 border-b border-border">
                            {menu.map((item) => (
                                <Skeleton key={item} className="h-10 w-24" />
                            ))}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <Skeleton className="h-64 w-full" />
                        <Skeleton className="h-48 w-full" />
                        <Skeleton className="h-32 w-full" />
                    </div>
                </div>
            </div>
        );
    }

    const getListingStatus = (listing: any) => {
        if (listing.closed === 1) {
            return {
                label: "Closed",
                className: "bg-gray-100 text-gray-700 border-gray-300"
            }
        }
    
        if (listing.active === 1 && listing.published === 1 && listing.closed === 0) {
            return {
                label: "Active",
                className: "bg-green-50 text-green-700 border-green-200"
            }
        }
    
        if (
            listing.active === 1 &&
            listing.published === 0 &&
            listing.closed === 0 &&
            listing.requireOwnerValidation === 1
        ) {
            return {
                label: "Pending Owner",
                className: "bg-yellow-50 text-yellow-800 border-yellow-200"
            }
        }
    
        if (listing.active === 1 && listing.published === 0 && listing.closed === 0) {
            return {
                label: "Pending",
                className: "bg-yellow-50 text-yellow-700 border-yellow-200"
            }
        }
    
        return {
            label: "Unknown",
            className: "bg-gray-100 text-gray-600 border-gray-300"
        }
    }
    const status = getListingStatus(listing);

    return listing && (
        <div className="min-h-screen bg-background">
            <div className="max-w-7xl mx-auto">
                <div className="mb-8">
                    <div className="mb-3 flex items-start justify-between">
                        <div>
                            <h1 className="text-lg! md:text-2xl! font-semibold text-foreground mb-2">{listing.headline}</h1>
                            <div className="flex items-center gap-3">
                                <Badge variant={listing.forSaleOrRentId === 1 ? "default" : "secondary"}>
                                    {listing.forSaleOrRentId === 1 ? 'FOR SALE' : 'FOR RENT'}
                                </Badge>
                            </div>
                        </div>
                        <div className="text-right">
                            {listing.createdBy === agent.id && (
                                <div className="flex items-center justify-end mb-2">
                                    <AIMarketingPanel listingId={Number(listingId)} />
                                </div>
                            )}
                            <p className="flex items-center gap-2 text-sm text-muted-foreground">
                                Ref: {listing.listingReferenceNumber}
                                {listing.createChannel === 'ai' && (
                                    listing.aiSessionPublicId ? (
                                        <Link to={`/listings/add?mode=ai&session=${listing.aiSessionPublicId}`} title='Open the AI conversation this listing came from'>
                                            <Badge variant='outline' className='gap-1 border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100'>
                                                <Sparkles className='h-3 w-3' /> Created with AI · open conversation
                                            </Badge>
                                        </Link>
                                    ) : (
                                        <Badge variant='outline' className='gap-1 border-violet-200 bg-violet-50 text-violet-700' title='Created with AI'>
                                            <Sparkles className='h-3 w-3' /> Created with AI
                                        </Badge>
                                    )
                                )}
                            </p>
                            {
                                listing.expiryDate && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Expires: {format(new Date(listing.expiryDate), 'MMM dd, yyyy')}
                                    </p>
                                )
                            }
                            {(listing.coShareListing === 1 || listing.coAgencyListing === 1) && (listing.createdBy === agent.id) && (
                                <div className="flex items-center justify-end gap-2 mt-2">
                                    {listing.coShareListing === 1 && (
                                        <Sheet open={showCoShareSheet} onOpenChange={setShowCoShareSheet}>
                                            <SheetTrigger asChild>
                                                <Button variant={'outline'} size={'sm'}>
                                                    <HandshakeIcon size={14} />
                                                    <span className="text-xs font-medium">Co-Share</span>
                                                    {pendingCoShareRequestsCount > 0 && (
                                                        <Badge 
                                                            variant="default" 
                                                            className="ml-1 h-4 w-4 flex items-center justify-center p-0 text-[10px] bg-blue-500">
                                                            {pendingCoShareRequestsCount > 9 ? '9+' : pendingCoShareRequestsCount}
                                                        </Badge>
                                                    )}
                                                </Button>
                                            </SheetTrigger>
                                            <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
                                                <SheetHeader className="p-6 pb-4 border-b">
                                                    <SheetTitle className="flex items-center gap-2">
                                                        <HandshakeIcon size={20} />
                                                        Co-Share Management
                                                    </SheetTitle>
                                                    <SheetDescription>
                                                        {pendingCoShareRequestsCount > 0 
                                                            ? `${pendingCoShareRequestsCount} pending request${pendingCoShareRequestsCount > 1 ? 's' : ''}` 
                                                            : 'Manage co-share agents, requests and invitations'}
                                                    </SheetDescription>
                                                </SheetHeader>

                                                <div className="px-6 py-3 border-b bg-gray-50">
                                                    <div className="relative">
                                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                        <Input
                                                        placeholder="Search agents to invite..."
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        className="pl-9"
                                                        disabled={listing.closed ? true : false}
                                                        />
                                                    </div>
                                                    
                                                    {searchQuery && (
                                                        <div className="mt-2 max-h-48 overflow-y-auto border rounded-md bg-white">
                                                            {searchingAgents ? (
                                                                <div className="p-4 text-center">
                                                                    <Loader2 className="w-4 h-4 animate-spin mx-auto text-muted-foreground" />
                                                                </div>
                                                            ) : searchResults.length === 0 ? (
                                                                <div className="p-4 text-center text-sm text-muted-foreground">
                                                                    No agents found
                                                                </div>
                                                            ) : (
                                                                <div className="divide-y">
                                                                    {searchResults.map((searchAgent) => (
                                                                        <div
                                                                            key={searchAgent.id}
                                                                            className="p-3 hover:bg-gray-50 flex items-center justify-between gap-3"
                                                                        >
                                                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                                <Avatar className="w-8 h-8">
                                                                                    <AvatarImage src={`${import.meta.env.VITE_AWS_S3}/${searchAgent.avatar}`} />
                                                                                    <AvatarFallback>
                                                                                        {searchAgent.firstName[0]}{searchAgent.lastName[0]}
                                                                                    </AvatarFallback>
                                                                                </Avatar>
                                                                                <div className="flex-1 min-w-0">
                                                                                    <p className="text-sm font-medium truncate">
                                                                                        {searchAgent.firstName} {searchAgent.lastName}
                                                                                    </p>
                                                                                    <p className="text-xs text-muted-foreground">
                                                                                        {searchAgent.renNo}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                            <Button
                                                                                size="sm"
                                                                                variant="outline"
                                                                                onClick={() => handleAddCoShareClick(searchAgent.id, searchAgent.firstName, searchAgent.lastName)}
                                                                                disabled={addingAgent[searchAgent.id]}
                                                                            >
                                                                                {addingAgent[searchAgent.id] ? (
                                                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                                                ) : (
                                                                                    <>
                                                                                        <UserPlus className="w-3 h-3 mr-1" />
                                                                                        Invite
                                                                                    </>
                                                                                )}
                                                                            </Button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                <div className='flex-1 overflow-y-auto p-4'>
                                                    <div className="mb-6">
                                                        <h3 className="text-sm font-semibold mb-3">Requests</h3>
                                                        {
                                                            loadingCoShare ? (
                                                                <div className='space-y-4'>
                                                                    {[1, 2].map((i) => (
                                                                        <Card key={i}>
                                                                            <CardContent className="p-4">
                                                                                <div className="flex items-start gap-3">
                                                                                    <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse" />
                                                                                    <div className="flex-1 space-y-2">
                                                                                        <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                                                                                        <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
                                                                                        <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3" />
                                                                                    </div>
                                                                                </div>
                                                                            </CardContent>
                                                                        </Card>
                                                                    ))}
                                                                </div>
                                                            ) : coShareRequests.filter(r => r.createdBy !== agent.id ).length === 0 ? (
                                                                <div className='flex flex-col items-center justify-center p-8 text-gray-400'>
                                                                    <HandshakeIcon size={48} className='mb-3 opacity-20' />
                                                                    <p className='text-sm font-medium'>No incoming requests</p>
                                                                    <p className='text-xs text-center mt-1'>
                                                                        No agents have requested to join this listing.
                                                                    </p>
                                                                </div>
                                                            ) : (
                                                                <div className='space-y-4'>
                                                                    {coShareRequests.filter(r => r.createdBy !== agent.id).map((request) => (
                                                                        <Card key={request.id}>
                                                                            <CardContent className="p-4">
                                                                                <div className="flex items-start gap-3">
                                                                                    <Avatar className="w-12 h-12">
                                                                                        <AvatarImage src={`${import.meta.env.VITE_AWS_S3}/${request.agent.avatar}`} />
                                                                                        <AvatarFallback>
                                                                                            {request.agent.firstName[0]}{request.agent.lastName[0]}
                                                                                        </AvatarFallback>
                                                                                    </Avatar>
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <div className="flex items-start justify-between gap-2 mb-2">
                                                                                            <div>
                                                                                                <p className="font-semibold text-sm">
                                                                                                    {request.agent.firstName} {request.agent.lastName}
                                                                                                </p>
                                                                                                <p className="text-xs text-muted-foreground">
                                                                                                    {request.agent.renNo}
                                                                                                </p>
                                                                                            </div>
                                                                                            <div className='flex items-center'>
                                                                                                {getStatusBadge(request.status)}
                                                                                            </div>
                                                                                        </div>
                                                                                        
                                                                                        <div className="space-y-1.5 mb-3">
                                                                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                                                                <Calendar className="w-3.5 h-3.5" />
                                                                                                <span>Requested {format(new Date(request.createdAt), 'MMM dd, yyyy')}</span>
                                                                                            </div>
                                                                                        </div>
                                                                                        {
                                                                                            request.status === 0 && (
                                                                                                <div className="flex gap-2">
                                                                                                    {processingRequests[request.id] === 'approving' ? (
                                                                                                        <Button 
                                                                                                            size="sm" 
                                                                                                            className="w-full bg-green-500 hover:bg-green-600 transition-all duration-500 ease-in-out"
                                                                                                            disabled>
                                                                                                            <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                                                                                            Approved!
                                                                                                        </Button>
                                                                                                    ) : processingRequests[request.id] === 'rejecting' ? (
                                                                                                        <Button 
                                                                                                            size="sm" 
                                                                                                            className="w-full bg-red-500 hover:bg-red-600 transition-all duration-500 ease-in-out"
                                                                                                            disabled>
                                                                                                            <XCircle className="w-3.5 h-3.5 mr-1" />
                                                                                                            Rejected!
                                                                                                        </Button>
                                                                                                    ) : (
                                                                                                        <>
                                                                                                            <Button 
                                                                                                                size="sm" 
                                                                                                                className="flex-1 transition-all duration-300"
                                                                                                                onClick={() => handleApproveRequestClick(request.id, request.agent.firstName, request.agent.lastName)}
                                                                                                                disabled={!!processingRequests[request.id]}>
                                                                                                                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                                                                                                Approve
                                                                                                            </Button>
                                                                                                            <Button 
                                                                                                                size="sm" 
                                                                                                                variant="outline"
                                                                                                                className="flex-1 transition-all duration-300"
                                                                                                                onClick={() => handleRejectRequestClick(request.id, request.agent.firstName, request.agent.lastName)}
                                                                                                                disabled={!!processingRequests[request.id]}>
                                                                                                                <XCircle className="w-3.5 h-3.5 mr-1" />
                                                                                                                Reject
                                                                                                            </Button>
                                                                                                        </>
                                                                                                    )}
                                                                                                </div>
                                                                                            )
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                            </CardContent>
                                                                        </Card>
                                                                    ))}
                                                                </div>
                                                            )
                                                        }
                                                    </div>

                                                    <div>
                                                        <h3 className="text-sm font-semibold mb-3">Sent Invitations</h3>
                                                        {
                                                            loadingCoShare ? (
                                                                <div className='space-y-4'>
                                                                    {[1, 2].map((i) => (
                                                                        <Card key={i}>
                                                                            <CardContent className="p-4">
                                                                                <div className="flex items-start gap-3">
                                                                                    <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse" />
                                                                                    <div className="flex-1 space-y-2">
                                                                                        <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                                                                                        <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
                                                                                        <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3" />
                                                                                    </div>
                                                                                </div>
                                                                            </CardContent>
                                                                        </Card>
                                                                    ))}
                                                                </div>
                                                            ) : coShareRequests.filter(r => r.createdBy === agent.id).length === 0 ? (
                                                                <div className='flex flex-col items-center justify-center p-8 text-gray-400'>
                                                                    <UserPlus size={48} className='mb-3 opacity-20' />
                                                                    <p className='text-sm font-medium'>No sent invitations</p>
                                                                    <p className='text-xs text-center mt-1'>
                                                                        Use the search above to invite agents.
                                                                    </p>
                                                                </div>
                                                            ) : (
                                                                <div className='space-y-4'>
                                                                    {coShareRequests.filter(r => r.createdBy === agent.id).map((request) => (
                                                                        <Card key={request.id}>
                                                                            <CardContent className="p-4">
                                                                                <div className="flex items-start gap-3">
                                                                                    <Avatar className="w-12 h-12">
                                                                                        <AvatarImage src={`${import.meta.env.VITE_AWS_S3}/${request.agent.avatar}`} />
                                                                                        <AvatarFallback>
                                                                                            {request.agent.firstName[0]}{request.agent.lastName[0]}
                                                                                        </AvatarFallback>
                                                                                    </Avatar>
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <div className="flex items-start justify-between gap-2 mb-2">
                                                                                            <div>
                                                                                                <p className="font-semibold text-sm">
                                                                                                    {request.agent.firstName} {request.agent.lastName}
                                                                                                </p>
                                                                                                <p className="text-xs text-muted-foreground">
                                                                                                    {request.agent.renNo}
                                                                                                </p>
                                                                                            </div>
                                                                                            <div>
                                                                                                {getStatusBadge(request.status)}
                                                                                            </div>
                                                                                        </div>
                                                                                        
                                                                                        <div className="space-y-1.5 mb-3">
                                                                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                                                                <Calendar className="w-3.5 h-3.5" />
                                                                                                <span>Invited {format(new Date(request.createdAt), 'MMM dd, yyyy')}</span>
                                                                                            </div>
                                                                                        </div>
                                                                                        {
                                                                                            request.status === 0 && (
                                                                                                <Button 
                                                                                                    size="sm" 
                                                                                                    variant="outline"
                                                                                                    className="w-full"
                                                                                                    onClick={() => cancelCoShareInvite(request.id)}>
                                                                                                    <XCircle className="w-3.5 h-3.5 mr-1" />
                                                                                                    Cancel Invitation
                                                                                                </Button>
                                                                                            )
                                                                                        }
                                                                                        {
                                                                                            request.status === 1 && (
                                                                                                <Button 
                                                                                                    size="sm" 
                                                                                                    variant="outline"
                                                                                                    className="w-full border-red-300 text-red-700 hover:bg-red-50"
                                                                                                    onClick={() => removeCoShareAgent(request.id)}>
                                                                                                    <Trash className="w-3.5 h-3.5 mr-1" />
                                                                                                    Remove Agent
                                                                                                </Button>
                                                                                            )
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                            </CardContent>
                                                                        </Card>
                                                                    ))}
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                            </SheetContent>
                                        </Sheet>
                                    )
                                    }
                                    {
                                        listing.coAgencyListing === 1 && (
                                            <Sheet open={showCoAgencySheet} onOpenChange={setShowCoAgencySheet}>
                                                <SheetTrigger asChild>
                                                    <Button variant={'outline'} size={'sm'}>
                                                        <Building2 size={14} />
                                                        <span className="text-xs font-medium">Co-Agency</span>
                                                    </Button>
                                                </SheetTrigger>
                                                <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
                                                    <SheetHeader className="p-6 pb-4 border-b">
                                                        <SheetTitle className="flex items-center gap-2">
                                                            <Building2 size={20} />
                                                            Co-Agency Management
                                                        </SheetTitle>
                                                        <SheetDescription>
                                                            Manage co-agency agents and invitations
                                                        </SheetDescription>
                                                    </SheetHeader>

                                                    <div className="px-6 py-3 border-b bg-gray-50">
                                                        <div className="relative">
                                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                            <Input
                                                            placeholder="Search agents to invite..."
                                                            value={searchQuery}
                                                            onChange={(e) => setSearchQuery(e.target.value)}
                                                            className="pl-9"
                                                            disabled={listing.closed ? true : false}
                                                            />
                                                        </div>
                                                        
                                                        {searchQuery && (
                                                            <div className="mt-2 max-h-48 overflow-y-auto border rounded-md bg-white">
                                                                {searchingAgents ? (
                                                                    <div className="p-4 text-center">
                                                                        <Loader2 className="w-4 h-4 animate-spin mx-auto text-muted-foreground" />
                                                                    </div>
                                                                ) : searchResults.length === 0 ? (
                                                                    <div className="p-4 text-center text-sm text-muted-foreground">
                                                                        No agents found
                                                                    </div>
                                                                ) : (
                                                                    <div className="divide-y">
                                                                        {searchResults.map((searchAgent) => (
                                                                            <div
                                                                                key={searchAgent.id}
                                                                                className="p-3 hover:bg-gray-50 flex items-center justify-between gap-3"
                                                                            >
                                                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                                    <Avatar className="w-8 h-8">
                                                                                        <AvatarImage src={`${import.meta.env.VITE_AWS_S3}/${searchAgent.avatar}`} />
                                                                                        <AvatarFallback>
                                                                                            {searchAgent.firstName[0]}{searchAgent.lastName[0]}
                                                                                        </AvatarFallback>
                                                                                    </Avatar>
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <p className="text-sm font-medium truncate">
                                                                                            {searchAgent.firstName} {searchAgent.lastName}
                                                                                        </p>
                                                                                        <p className="text-xs text-muted-foreground">
                                                                                            {searchAgent.renNo}
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                                <Button
                                                                                    size="sm"
                                                                                    variant="outline"
                                                                                    onClick={() => handleAddCoAgency(searchAgent.id)}
                                                                                    disabled={addingAgent[searchAgent.id]}
                                                                                >
                                                                                    {addingAgent[searchAgent.id] ? (
                                                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                                                    ) : (
                                                                                        <>
                                                                                            <UserPlus className="w-3 h-3 mr-1" />
                                                                                            Invite
                                                                                        </>
                                                                                    )}
                                                                                </Button>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    <div className='flex-1 overflow-y-auto p-4'>
                                                        <h3 className="text-sm font-semibold mb-3">Invited Agents</h3>
                                                        {
                                                            loadingCoAgency ? (
                                                                <div className='space-y-4'>
                                                                    {[1, 2, 3].map((i) => (
                                                                        <Card key={i}>
                                                                            <CardContent className="p-4">
                                                                                <div className="flex items-start gap-3">
                                                                                    <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse" />
                                                                                    <div className="flex-1 space-y-2">
                                                                                        <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                                                                                        <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
                                                                                        <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3" />
                                                                                    </div>
                                                                                </div>
                                                                            </CardContent>
                                                                        </Card>
                                                                    ))}
                                                                </div>
                                                            ) : coAgencyRequests.length === 0 ? (
                                                                <div className='flex flex-col items-center justify-center h-full p-8 text-gray-400'>
                                                                    <Building2 size={64} className='mb-4 opacity-20' />
                                                                    <p className='text-lg font-medium'>No co-agency agents</p>
                                                                    <p className='text-sm text-center mt-2'>
                                                                        Use the search above to invite agents to this co-agency listing.
                                                                    </p>
                                                                </div>
                                                            ) : (
                                                                <div className='space-y-4'>
                                                                    {coAgencyRequests.map((request) => (
                                                                        <Card key={request.id}>
                                                                            <CardContent className="p-4">
                                                                                <div className="flex items-start gap-3">
                                                                                    <Avatar className="w-12 h-12">
                                                                                        <AvatarImage src={`${import.meta.env.VITE_AWS_S3}/${request.agent.avatar}`} />
                                                                                        <AvatarFallback>
                                                                                            {request.agent.firstName[0]}{request.agent.lastName[0]}
                                                                                        </AvatarFallback>
                                                                                    </Avatar>
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <div className="flex items-start justify-between gap-2 mb-2">
                                                                                            <div>
                                                                                                <p className="font-semibold text-sm">
                                                                                                    {request.agent.firstName} {request.agent.lastName}
                                                                                                </p>
                                                                                                <p className="text-xs text-muted-foreground">
                                                                                                    {request.agent.renNo}
                                                                                                </p>
                                                                                            </div>
                                                                                            <div>
                                                                                                {getStatusBadge(request.status)}
                                                                                            </div>
                                                                                        </div>
                                                                                        
                                                                                        <div className="space-y-1.5 mb-3">
                                                                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                                                                <Calendar className="w-3.5 h-3.5" />
                                                                                                <span>Invited {format(new Date(request.createdAt), 'MMM dd, yyyy')}</span>
                                                                                            </div>
                                                                                        </div>
                                                                                        {
                                                                                            request.status === 0 && (
                                                                                                <Button 
                                                                                                    size="sm" 
                                                                                                    variant="outline"
                                                                                                    className="w-full"
                                                                                                    onClick={() => cancelCoAgencyInvite(request.id)}>
                                                                                                    <XCircle className="w-3.5 h-3.5 mr-1" />
                                                                                                    Cancel Invitation
                                                                                                </Button>
                                                                                            )
                                                                                        }
                                                                                        {
                                                                                            request.status === 1 && (
                                                                                                <Button 
                                                                                                    size="sm" 
                                                                                                    variant="outline"
                                                                                                    className="w-full border-red-300 text-red-700 hover:bg-red-50"
                                                                                                    onClick={() => removeCoAgencyAgent(request.id)}>
                                                                                                    <Trash className="w-3.5 h-3.5 mr-1" />
                                                                                                    Remove Agent
                                                                                                </Button>
                                                                                            )
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                            </CardContent>
                                                                        </Card>
                                                                    ))}
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                </SheetContent>
                                            </Sheet>
                                        )
                                    }
                                </div>
                            )}
                            {
                                <Dialog open={open} onOpenChange={setOpen}>
                                    <DialogTrigger asChild>
                                        <Button 
                                        variant={listing.closed === 1 ? `default` : `outline`} 
                                        size={'sm'}
                                        className="w-full mt-3 cursor-pointer!">
                                            {listing.closed === 1 ? 'Reopen' : 'Done deal'}
                                        </Button>
                                    </DialogTrigger>
                                
                                    <DialogContent className="sm:max-w-lg">
                                        <DialogHeader>
                                            <DialogTitle>{ listing.closed === 1 ? 'Reopen listing' : 'Complete This Deal?'}</DialogTitle>
                                            <DialogDescription>
                                                {
                                                    listing.closed === 1 ? 
                                                    `Are you sure you want to reopen this listing? This action will allow the
                                                    listing to be shown in public view.` 
                                                    :
                                                    `Please ensure the listing represents a completed deal. This action may remove it from public view.`
                                                }
                                            </DialogDescription>
                                        </DialogHeader>
                                        <DialogFooter>
                                            <Button variant="ghost" onClick={() => setOpen(false)}>
                                            Cancel
                                            </Button>
                                            <Button variant="default" onClick={listing.closed === 1 ? openListing : closeListing}>
                                                {
                                                    actionLoading ? 
                                                    <Spinner />
                                                    : 'Yes'
                                                }
                                            </Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>
                            }
                        </div>
                    </div>
                    <div className="gap-6 text-muted-foreground">
                        <div className="flex items-center gap-2">
                            <MapPin size={14} className="shrink-0" />
                            <span className="text-xs">{listing.property.rawAddress || 'N/A'}</span>
                        </div>

                        <div className="flex items-center gap-2 mt-3">
                            <Calendar size={14} className="shrink-0" />
                            <span className="text-xs">
                                Listed {format(new Date(listing.createdAt ?? ''), 'MMM dd, yyyy')}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                            <Timer size={14} className="shrink-0" />
                            <span className="text-xs text-gray-500">Status:</span>
                            <Badge className={`border ${status.className}`}>
                                {status.label}
                            </Badge>
                        </div>
                    </div>
                </div>
                <div className="mb-8">
                    <div className="md:flex relative border-b border-border">
                        {
                            menu.map(item => {
                                const isDisabled = !hasFullAccess() && item !== 'overview';
                                
                                return (
                                    <div
                                    key={item}
                                    onClick={() => handleTabClick(item)}
                                    className={`relative z-10 px-4 py-3 transition-colors ${
                                        isDisabled 
                                            ? 'cursor-not-allowed opacity-40' 
                                            : 'cursor-pointer'
                                    } ${
                                        activeTab === item ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                                    }`}>
                                        <span className={`relative z-10 uppercase text-xs font-medium tracking-wide ${
                                            activeTab === item ? 'text-foreground' : 'text-muted-foreground'
                                        }`}>
                                            {item}
                                        </span>
                                        {
                                            activeTab === item && (
                                                <motion.div
                                                layoutId="underline"
                                                className="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-foreground"
                                                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                                                />
                                            )
                                        }
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>
                {
                    activeTab === 'overview' ? (
                        <Details listing={listing}/>
                    ) : activeTab === 'stats' ? (
                        <Stats listing={listing}/>
                    ) : activeTab === 'visits' ? (
                        <Histories listing={listing}/>
                    ) : activeTab === 'proposal' ? (
                        <Proposal 
                        listing={listing}
                        setListing={setListing}/>
                    ) : activeTab === 'documents' ? (
                        <Documents listing={listing}/>
                    ) : activeTab === 'timeline' && (
                        <Timeline listing={listing}/>
                    )
                }
            </div>

            {/* Invite Confirmation Dialog */}
            <Dialog open={inviteConfirmDialog.open} onOpenChange={(open) => setInviteConfirmDialog({ open, agentId: null, agentName: '' })}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <HandshakeIcon className="w-5 h-5" />
                            Confirm Co-Share Invitation
                        </DialogTitle>
                        <DialogDescription>
                            You're about to invite {inviteConfirmDialog.agentName} to this co-share listing.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <Alert className="border-blue-200 bg-blue-50">
                        <AlertCircle className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-sm text-blue-900">
                            By inviting this agent, you agree to split the profits from this listing according to your co-share agreement.
                        </AlertDescription>
                    </Alert>

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button 
                            variant="outline" 
                            onClick={() => setInviteConfirmDialog({ open: false, agentId: null, agentName: '' })}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleAddCoShare}
                            disabled={addingAgent[inviteConfirmDialog.agentId || 0]}>
                            {addingAgent[inviteConfirmDialog.agentId || 0] ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                'Confirm & Send Invitation'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={approveConfirmDialog.open} onOpenChange={(open) => setApproveConfirmDialog({ open, requestId: null, agentName: '' })}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            Approve Co-Share Request
                        </DialogTitle>
                        <DialogDescription>
                            You're about to approve {approveConfirmDialog.agentName}'s request to join this co-share listing.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <Alert className="border-green-200 bg-green-50">
                        <AlertCircle className="h-4 w-4 text-green-600" />
                        <AlertDescription className="text-sm text-green-900">
                            By approving this request, you agree to split the profits from this listing with {approveConfirmDialog.agentName} according to your co-share agreement.
                        </AlertDescription>
                    </Alert>

                    <DialogFooter className="gap-2 gap-1">
                        <Button 
                            variant="outline" 
                            onClick={() => setApproveConfirmDialog({ open: false, requestId: null, agentName: '' })}>
                            Cancel
                        </Button>
                        <Button 
                        onClick={handleApproveRequest}
                        disabled={processingRequests[approveConfirmDialog.requestId || 0] === 'approving'}>
                        {processingRequests[approveConfirmDialog.requestId || 0] === 'approving' ? (
                            <Spinner />
                        ) : (
                            'Approve'
                        )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={rejectConfirmDialog.open} onOpenChange={(open) => setRejectConfirmDialog({ open, requestId: null, agentName: '' })}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            Confirm Action
                        </DialogTitle>
                        <DialogDescription>
                            This action will decline the co-share request. The agent will be notified of your decision.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <DialogFooter className="gap-2 gap-1">
                        <Button 
                            variant="outline" 
                            onClick={() => setRejectConfirmDialog({ open: false, requestId: null, agentName: '' })}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleRejectRequest}
                            disabled={processingRequests[rejectConfirmDialog.requestId || 0] === 'rejecting'}>
                            {processingRequests[rejectConfirmDialog.requestId || 0] === 'rejecting' ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Rejecting...
                                </>
                            ) : (
                                'Confirm'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ListingView;